<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6">
        <BreadcrumbMenu />
      </b-col>
    </b-row>
    <b-card class="mx-2" no-body>
      <b-row class="m-2">
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label class="text-capitalize">{{ $t("table.show") }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block mx-50"
          />
          <label class="text-capitalize">{{ $t("table.entries") }}</label>
        </b-col>

        <!-- Search -->
        <b-col cols="12" md="6">
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('table.search') + '...'"
            />
            <b-button
              variant="secondary"
              to="/risk/templates/builder"
              v-if="$can('create', 'RiskTemplate')"
            >
              <span class="text-nowrap text-capitalize">
                {{ $t("actions.add") + " Template" }}
              </span>
            </b-button>
          </div>
        </b-col>
      </b-row>
      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(name)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="$can('update', 'RiskTemplate')"
              :to="`/risk/templates/builder/${data.item.id}`"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("actions.edit") }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('delete', 'RiskTemplate')"
              @click="confirmDelete(data.item.id)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("actions.delete") }}
              </span>
            </b-dropdown-item>

            <!-- Show risk modal option -->
            <b-dropdown-item @click="showRiskForm(data.item.id)">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50 text-capitalize">
                {{ $t("actions.show") }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <ShowRiskFormModal
      v-if="selectedTemplateId"
      :template-id="selectedTemplateId"
      :risk-evaluation-id="selectedRiskEvaluationId"
      :id-unit="selectedIdUnit"
      @onSubmitFormBooklet="onSubmitFormBooklet"
      @onFormValidated="onFormValidated"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BDropdown,
  BDropdownItem,
  BTable,
} from "bootstrap-vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import { ref, getCurrentInstance } from "@vue/composition-api";
import useList from "./useList";
import vSelect from "vue-select";
import Swal from "sweetalert2";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Pagination from "@/components/Pagination.vue";
import ShowRiskFormModal from "@/components/ShowRiskFormModal.vue";

export default {
  components: {
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BDropdown,
    BDropdownItem,
    BTable,
    Pagination,
    vSelect,
    ToastificationContent,
    ShowRiskFormModal,
  },
  setup() {
    const vm = getCurrentInstance().proxy;

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      filtersCount,
      clearSearchFilters,
      buildFilters,
    } = useList();

    const toast = useToast();
    const selectedTemplateId = ref(null);
    const selectedRiskEvaluationId = ref(null);
    const selectedIdUnit = ref(null);

    const confirmDelete = (id) => {
      Swal.fire({
        title: "¿Estás seguro de eliminar el template?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          axios.delete(`/v1/risk_templates/${id}`).then(() => {
            refetchData();
            toast({
              component: ToastificationContent,
              props: {
                title: "Template eliminado correctamente",
                icon: "CheckIcon",
                variant: "success",
              },
            });
          });
        }
      });
    };

    const update = (data) => {
      currentPage.value = data;
    };

    const showRiskForm = async (templateId) => {
      selectedTemplateId.value = templateId;
      selectedRiskEvaluationId.value = null;    // We dont have evaluation id, we create it the show
      selectedIdUnit.value = null;

      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "risk_form_modal");
    };

    const onSubmitFormBooklet = () => {
      //Here if you want to add some actions after saving the form cris.
    };

    const onFormValidated = () => {
      //Here fi you want to add some action after validated in this case reviewed cris.
    };

    return {
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      confirmDelete,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      selectedTemplateId,
      selectedRiskEvaluationId,
      selectedIdUnit,
      showRiskForm,
      onSubmitFormBooklet,
      onFormValidated,
    };
  },
};
</script>
